/* eslint-disable camelcase */
import { enhanceLogisticOffersForConfig } from '@invivodf/module-logistic-offer-client-sdk'
import camelCase from 'lodash/camelCase'
import snakeCase from 'lodash/snakeCase'
import { OfferV3Dto, OfferV3Payload } from '@b2ag/module-offers-interfaces'

export function decomposeOfferId(id: string) {
  if (!id || typeof id !== 'string' || (typeof id === 'string' && (!id.length || !/:/g.test(id)))) {
    return {}
  }
  return {
    variantInvivoId: id.split(':')[1],
    sellerCustomId: id.split(':')[2],
  }
}

const isEmptyValue = (value) => value === undefined || value === null || value === ''

function mapNegotiationToApiV3({ negotiationPriceDecrease, negotiationPriceIncrease, negotiationUnit }) {
  if (isEmptyValue(negotiationPriceDecrease) && isEmptyValue(negotiationPriceIncrease)) {
    return { price_decrease: null, price_increase: null, unit: null }
  }
  return {
    price_decrease: isEmptyValue(negotiationPriceDecrease) ? null : +negotiationPriceDecrease,
    price_increase: isEmptyValue(negotiationPriceIncrease) ? null : +negotiationPriceIncrease,
    unit: negotiationUnit || null,
  }
}

export function mapFromApiV3(offer = {} as OfferV3Dto) {
  const { variantInvivoId, sellerCustomId } = decomposeOfferId(offer.offer_id)
  return {
    id: offer.offer_id,
    measurementPrice: offer.base_measurement_price,
    unitPrice: offer.base_unit_price,
    sellerCustomId,
    isTechExclusive: offer.is_tech_exclusive,
    isExclusive: offer.is_exclusive,
    subscriptionAllowed: offer.subscription_allowed,
    validationRequired: offer.validation_required,
    negotiationPriceDecrease: offer.negotiation?.price_decrease?.toString(),
    negotiationPriceIncrease: offer.negotiation?.price_increase?.toString(),
    negotiationUnit: offer.negotiation?.unit,
    shippingStartDate: offer.shipping_start_date,
    shippingEndDate: offer.shipping_end_date,
    shippingMethodList: offer.shipping_method_list,
    validityStartDate: offer.validity_start_date,
    validityEndDate: offer.validity_end_date,
    validityStatus: offer.validity_status,
    priceType: camelCase(offer.price_type),
    quantityMin: offer.quantity_min?.toString(),
    quantityMax: offer.quantity_max?.toString(),
    quantityStep: offer.quantity_step?.toString(),
    intervalName: offer.interval_name,
    segmentList: offer.segment_list,
    offerType: offer.type ?? '',
    description: offer.description,
    allowedWithoutStock: offer.allowed_without_stock,
    measureUnit: offer.measure_unit,
    offerTechnicalInformation: offer.technical_information,
    variantInvivoId,
    measureUnitPrice: offer.measure_unit_price?.toString(),
    paymentDueDateReferences: offer.payment_due_date_references,
    salesUnit: offer.sales_unit,
    restrictedSalesMax: offer.restricted_sales_max?.toString(),
    restrictedSalesMin: offer.restricted_sales_min?.toString(),
  }
}

export function mapToApiV3(offer: any = {}): OfferV3Payload {
  return {
    variant_id: offer.variantId,
    base_measurement_price: offer.measurementPrice,
    base_unit_price: offer.unitPrice,
    seller_custom_id: offer.sellerCustomId,
    is_tech_exclusive: offer.isTechExclusive,
    is_exclusive: offer.isExclusive,
    subscription_allowed: offer.subscriptionAllowed,
    validation_required: offer.validationRequired,
    // @ts-ignore OfferV3Payload ne type pas null, alors que l'API l'accepte pour supprimer les bornes
    negotiation: mapNegotiationToApiV3(offer),
    shipping_start_date: offer.shippingStartDate || '',
    shipping_end_date: offer.shippingEndDate || '',
    shipping_method_list: offer.shippingMethodList,
    validity_start_date: offer.validityStartDate,
    validity_end_date: offer.validityEndDate || '',
    // @ts-ignore EXPLIQUEZ POURQUOI VOUS IGNOREZ CETTE ERREUR
    price_type: snakeCase(offer.priceType),
    quantity_min: +offer.quantityMin || undefined,
    quantity_max: +offer.quantityMax || undefined,
    quantity_step: +offer.quantityStep || undefined,
    interval_name: offer.intervalName?.trim() || undefined,
    segment_list: offer.segmentList,
    type: offer.offerType || undefined,
    description: offer.description || undefined,
    allowed_without_stock: offer.allowedWithoutStock,
    technical_information: offer.offerTechnicalInformation || '',
    measure_unit_price: +offer.measureUnitPrice,
    measure_unit: offer.measureUnit,
    payment_due_date_references: offer.paymentDueDateReferences,
    sales_unit: offer.salesUnit,
    restricted_sales_max: +offer.restrictedSalesMax || undefined,
    restricted_sales_min: +offer.restrictedSalesMin || undefined,
  }
}

export function mapListFromApi(offers: any[] = []) {
  return offers.map((offer) => {
    const { variantInvivoId, sellerCustomId } = decomposeOfferId(offer.offerId)

    return {
      id: offer.offerId,
      measurementPrice: offer.baseMeasurementPrice,
      unitPrice: offer.baseUnitPrice,
      sellerCustomId,
      isTechExclusive: offer.isTechExclusive,
      isExclusive: offer.isExclusive,
      subscriptionAllowed: offer.subscriptionAllowed,
      validationRequired: offer.validationRequired,
      negotiationPriceDecrease: offer.negotiation?.priceDecrease?.toString(),
      negotiationPriceIncrease: offer.negotiation?.priceIncrease?.toString(),
      negotiationUnit: offer.negotiation?.unit,
      shippingStartDate: offer.shippingStartDate,
      shippingEndDate: offer.shippingEndDate,
      shippingMethodList: offer.shippingMethodList,
      logisticOffers: enhanceLogisticOffersForConfig(offer.logisticOffers),
      validityStartDate: offer.validityStartDate,
      validityEndDate: offer.validityEndDate,
      validityStatus: offer.validityStatus,
      priceType: camelCase(offer.priceType),
      quantityMin: offer.quantityMin?.toString(),
      quantityMax: offer.quantityMax?.toString(),
      quantityStep: offer.quantityStep?.toString(),
      intervalName: offer.intervalName,
      segmentList: offer.segmentList,
      offerType: offer.type || '',
      description: offer.description,
      allowedWithoutStock: offer.allowedWithoutStock,
      measureUnit: offer.measureUnit,
      offerTechnicalInformation: offer.technicalInformation,
      variantInvivoId,
      measureUnitPrice: offer.measureUnitPrice?.toString(),
      paymentDueDateReferences: offer.paymentDueDateReferences,
      restrictedSalesMax: offer.restrictedSalesMax?.toString(),
      restrictedSalesMin: offer.restrictedSalesMin?.toString(),
      salesUnit: offer.salesUnit,
    }
  })
}
